import type { QueryRowType } from "@rocicorp/zero";
import { styled } from "goober";
import { useEffect, useMemo, useRef } from "react";
import { queries } from "../../../shared/queries";
import type { IconName } from "../../assets/icons";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useQuery } from "@rocicorp/zero/react";
import { useUrlSort } from "../../hooks/useUrlSort";
import { routes } from "../../lib/urls";
import { mainBranchConnectorIds } from "../../lib/connector";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar } from "../Avatar";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ListTable } from "../ListTable";
import { Text } from "../Text";

const ScorePill = styled("div")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 16px;
`;

const SHINE_GRADIENT =
  "linear-gradient(var(--shine, 0deg), rgba(255, 255, 255, 0) 35%, rgba(255, 255, 255, 0.25) 50%, rgba(255, 255, 255, 0) 65%)";

function useCursorShine(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame: number | null = null;
    let cx = 0;
    let cy = 0;
    const update = () => {
      frame = null;
      const r = el.getBoundingClientRect();
      const angle =
        Math.atan2(cy - (r.top + r.height / 2), cx - (r.left + r.width / 2)) *
          (180 / Math.PI) +
        90;
      el.style.setProperty("--shine", `${angle}deg`);
    };
    const onMove = (e: MouseEvent) => {
      cx = e.clientX;
      cy = e.clientY;
      if (frame == null) frame = requestAnimationFrame(update);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [ref]);
}

type SpaceDetailData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadSpace>>
>;
type SpaceDetailWorkflow = SpaceDetailData["workflows"][number];

type SpaceAppsWorkflow = SpaceDetailWorkflow;

type LeaderboardUser = { id: string; name: string | null; email: string };

type Props = {
  spaceId: string;
  workflows: readonly SpaceDetailWorkflow[];
  pagesWorkflows: readonly SpaceAppsWorkflow[];
  extraMembers: readonly LeaderboardUser[];
};

type Stats = {
  user: LeaderboardUser;
  workflowsBuilt: number;
  lastBuilt: number | null;
  pagesShipped: number;
  appsUsed: number;
  branches: number;
  messagesSent: number;
  score: number;
};

function computeScore(s: Omit<Stats, "user" | "score">): number {
  return Math.round(
    s.workflowsBuilt * 10 +
      s.pagesShipped * 15 +
      s.appsUsed * 5 +
      s.branches * 2 +
      s.messagesSent * 0.5
  );
}

const PODIUM: Array<{ icon: IconName; bg: string }> = [
  { icon: "crown", bg: "var(--yellow-600)" },
  { icon: "trophy", bg: "var(--gray-600)" },
  { icon: "award", bg: "var(--orange-600)" },
];

function PodiumBadge({ medal }: { medal: (typeof PODIUM)[number] }) {
  const ref = useRef<HTMLDivElement>(null);
  useCursorShine(ref);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        bottom: -4,
        right: -4,
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor: medal.bg,
        backgroundImage: SHINE_GRADIENT,
        boxShadow: "0 0 0 2px var(--surface-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon name={medal.icon} size={10} color="white" />
    </div>
  );
}

function ShinyScorePill({ score, color }: { score: number; color: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useCursorShine(ref);

  return (
    <ScorePill
      ref={ref}
      style={{
        backgroundColor: color,
        backgroundImage: SHINE_GRADIENT,
        borderColor: color,
      }}
    >
      <Icon name="toy-brick" size={12} color="white" />
      <Text variant="text-small-md" color="white">
        {score.toLocaleString("en-US")}
      </Text>
    </ScorePill>
  );
}

export function SpaceLeaderboard({
  spaceId,
  workflows,
  pagesWorkflows,
  extraMembers,
}: Props) {
  const formatRelativeTime = useFormatRelativeTime();
  const isMobile = useIsMobile();
  const {
    sortColumn: sortColumnParam,
    sortDirection,
    handleSort,
  } = useUrlSort(routes.space.leaderboard, {
    name: "asc",
    workflowsBuilt: "desc",
    branches: "desc",
    appsUsed: "desc",
    pagesShipped: "desc",
    messagesSent: "desc",
    lastBuilt: "desc",
    score: "desc",
  });
  const sortColumn = sortColumnParam ?? "name";

  const [stats] = useQuery(
    queries.spaceUserStats({
      spaceId,
    })
  );

  const { rows, podiumByUserId, rankByUserId } = useMemo<{
    rows: Stats[];
    podiumByUserId: Map<string, (typeof PODIUM)[number]>;
    rankByUserId: Map<string, number>;
  }>(() => {
    const byUser = new Map<string, Stats>();

    const ensure = (userId: string, user: LeaderboardUser): Stats => {
      const existing = byUser.get(userId);
      if (existing) return existing;
      const fresh: Stats = {
        user,
        workflowsBuilt: 0,
        lastBuilt: null,
        pagesShipped: 0,
        appsUsed: 0,
        branches: 0,
        messagesSent: 0,
        score: 0,
      };
      byUser.set(userId, fresh);
      return fresh;
    };

    const appsByUser = new Map<string, Set<string>>();
    for (const w of workflows) {
      if (!w.createdByUserId || !w.createdBy) continue;
      const s = ensure(w.createdByUserId, w.createdBy);
      s.workflowsBuilt += 1;
      s.lastBuilt = Math.max(s.lastBuilt ?? 0, w.createdAt);
      const apps = mainBranchConnectorIds(w.branches);
      let set = appsByUser.get(w.createdByUserId);
      if (!set) {
        set = new Set();
        appsByUser.set(w.createdByUserId, set);
      }
      for (const a of apps) set.add(a);
    }
    for (const [userId, set] of appsByUser) {
      const s = byUser.get(userId);
      if (s) s.appsUsed = set.size;
    }

    for (const w of pagesWorkflows) {
      if (!w.createdByUserId || !w.createdBy) continue;
      const s = ensure(w.createdByUserId, w.createdBy);
      s.pagesShipped += 1;
    }

    for (const row of stats) {
      if (!row.user) continue;
      const s = ensure(row.userId, row.user);
      s.branches = row.branchCount ?? 0;
      s.messagesSent = row.messageCount ?? 0;
    }

    for (const m of extraMembers) {
      ensure(m.id, m);
    }

    for (const s of byUser.values()) {
      s.score = computeScore(s);
    }

    const all = [...byUser.values()];
    const ranked = [...all].sort(
      (a, b) =>
        b.score - a.score ||
        userDisplayName(a.user).localeCompare(userDisplayName(b.user))
    );
    const ranks = new Map<string, number>();
    for (let i = 0; i < ranked.length; i++) {
      const r = ranked[i];
      if (r) ranks.set(r.user.id, i + 1);
    }
    const podium = new Map<string, (typeof PODIUM)[number]>();
    const eligible = ranked.filter((s) => s.score > 0);
    for (let i = 0; i < Math.min(3, eligible.length); i++) {
      const winner = eligible[i];
      const medal = PODIUM[i];
      if (winner && medal) podium.set(winner.user.id, medal);
    }

    const sorted = all.sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      if (sortColumn === "name") {
        const ar = ranks.get(a.user.id) ?? 0;
        const br = ranks.get(b.user.id) ?? 0;
        return (ar - br) * dir;
      }
      const key = sortColumn as keyof Stats;
      const av = (a[key] as number | null) ?? 0;
      const bv = (b[key] as number | null) ?? 0;
      if (av === bv) {
        return userDisplayName(a.user).localeCompare(userDisplayName(b.user));
      }
      return (av - bv) * dir;
    });

    return { rows: sorted, podiumByUserId: podium, rankByUserId: ranks };
  }, [
    workflows,
    pagesWorkflows,
    extraMembers,
    stats,
    sortColumn,
    sortDirection,
  ]);

  const headers = isMobile
    ? { name: "Builder", score: "Builder score" }
    : {
        name: "Builder",
        workflowsBuilt: "Workflows",
        branches: "Branches",
        appsUsed: "Connectors used",
        pagesShipped: "Pages shipped",
        messagesSent: "Messages",
        lastBuilt: "Last built",
        score: "Builder score",
      };

  const sortableColumns = isMobile
    ? ["score"]
    : [
        "workflowsBuilt",
        "branches",
        "appsUsed",
        "pagesShipped",
        "messagesSent",
        "lastBuilt",
        "score",
      ];

  return (
    <ListTable
      columns={
        isMobile
          ? { score: "sm" }
          : {
              workflowsBuilt: "sm",
              branches: "sm",
              appsUsed: "sm",
              pagesShipped: "sm",
              messagesSent: "sm",
              lastBuilt: "md",
              score: "md",
            }
      }
      headers={headers}
      sortableColumns={sortableColumns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      emptyState={
        <EmptyState title="No members" iconName="circle-user-round" />
      }
      items={rows.map((r) => ({
        key: r.user.id,
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                user={r.user as Parameters<typeof Avatar>[0]["user"]}
                size={32}
              />
              {(() => {
                const medal = podiumByUserId.get(r.user.id);
                return medal ? <PodiumBadge medal={medal} /> : null;
              })()}
            </div>
            <Text
              ellipsis
              variant="text-regular-md"
              color="var(--text-primary)"
            >
              {rankByUserId.get(r.user.id)}. {userDisplayName(r.user)}
            </Text>
          </div>
        ),
        workflowsBuilt: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.workflowsBuilt.toLocaleString("en-US")}
          </Text>
        ),
        branches: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.branches.toLocaleString("en-US")}
          </Text>
        ),
        appsUsed: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.appsUsed.toLocaleString("en-US")}
          </Text>
        ),
        pagesShipped: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.pagesShipped.toLocaleString("en-US")}
          </Text>
        ),
        messagesSent: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.messagesSent.toLocaleString("en-US")}
          </Text>
        ),
        lastBuilt: (
          <Text variant="text-small" color="var(--text-primary)">
            {r.lastBuilt ? formatRelativeTime(r.lastBuilt) : "—"}
          </Text>
        ),
        score: (() => {
          const rank = rankByUserId.get(r.user.id) ?? 0;
          const medal = PODIUM[rank - 1];
          if (medal) {
            return <ShinyScorePill score={r.score} color={medal.bg} />;
          }
          return (
            <ScorePill
              style={{
                background: "var(--gray-200)",
                borderColor: "var(--gray-200)",
              }}
            >
              <Icon name="toy-brick" size={12} color="var(--text-secondary)" />
              <Text variant="text-small-md" color="var(--text-secondary)">
                {r.score.toLocaleString("en-US")}
              </Text>
            </ScorePill>
          );
        })(),
      }))}
    />
  );
}
